import { SURVEY_OBJECTION, numberedCountOf, surveyOf, surveyArmOf, surveyQuestionListOf, surveyVariantsOf } from "./questionnaire.js";
/**
 * Morning until noon, afternoon after. The hour is passed in; nothing here reads a clock.
 *
 * AMENDED 2026-10-21 (plan-026, design §37.4.4): a questionnaire that names an `evening` greeting, in a language
 * whose pack names its slot hours, greets in three slots — morning before `evening`, afternoon before `night`,
 * evening from `night`. Without either, today's two-slot rule exactly.
 */
export function timeGreetingOf(survey, hourOfDay, slotHour) {
    const evening = survey.greetings.evening;
    if (evening === undefined || slotHour === undefined || slotHour === null) {
        return hourOfDay < 12 ? survey.greetings.morning : survey.greetings.afternoon;
    }
    if (hourOfDay < slotHour.evening)
        return survey.greetings.morning;
    if (hourOfDay < slotHour.night)
        return survey.greetings.afternoon;
    return evening;
}
export function openingLineOf(survey, greeting) {
    return survey.opening.intro.replace("{greeting}", greeting);
}
/**
 * The sentence ending a question in any script: `?`, the Greek question mark `;` (U+037E) and the
 * semicolon a Greek keyboard types for it, the Arabic `؟`, the Armenian `՞`, the Ethiopic `፧` and the
 * full-width `？`.
 */
const QUESTION_END = /[?;;؟՞፧？]$/u;
/** A sentence boundary: after `.`, `!` or a question mark, before white space. */
const SENTENCE_BOUNDARY = /(?<=[.!?;;؟՞፧？。！])\s+/u;
/**
 * ADDED 2026-10-02 (the survey interviewer): the opening's own consent question — its last sentence,
 * when that sentence asks one ("Επιθυμείτε να συνεχίσουμε;", "Έχετε δύο λεπτά;") — or `null` when the
 * opening asks nothing and the first question follows it in one breath, as surveybot's did.
 *
 * Approved copy by construction: it is a sentence of `opening.intro`, never written here. The
 * interviewer waits for consent after an opening that asks for it, and asks this sentence again after
 * answering an objection or a garbled reply, rather than the whole introduction.
 */
export function consentQuestionOf(survey) {
    const sentence = survey.opening.intro.trim().split(SENTENCE_BOUNDARY);
    const last = sentence[sentence.length - 1];
    if (last === undefined || !QUESTION_END.test(last) || last.includes("{greeting}"))
        return null;
    return last;
}
/** The arm's question at `index` when it is a scale question (a v1 statement), else `null`. */
export function statementOf(survey, variant, index) {
    const found = surveyArmOf(survey, variant).questions[index];
    return found === undefined || found.type !== "scale" ? null : found;
}
/** How many numbered questions come before `index` — the position its ordinal is read from. */
export function numberedIndexOf(arm, index) {
    return arm.questions.slice(0, index).filter((question) => question.numbered).length;
}
/** "Πρώτη φράση." — the last ordinal says it is the last, which is why it is approved copy. */
export function ordinalOf(survey, numberedIndex, variant = "A") {
    const ordinal = surveyArmOf(survey, variant).ordinals;
    return ordinal[Math.min(numberedIndex, ordinal.length - 1)];
}
/** The one-time explanation before the first question, with the question count spelled out as a word. */
export function scaleIntroOf(survey, variant) {
    const count = numberedCountOf(surveyArmOf(survey, variant));
    return survey.scale.intro.replace("{count}", survey.count_words[count] ?? "");
}
/** Questions and their clarifications include the approved choices in the same spoken line. */
export function surveyChoiceLineOf(survey, question, line = "text") {
    const copy = question[line];
    const normal = (value) => value.toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");
    const text = normal(copy);
    // Recognition stems may also occur in the instructions (e.g. "news"), and one keyword
    // cannot prove that a composite label was read. Only a complete label avoids repetition.
    const missing = question.options.filter((option) => {
        const escaped = normal(option.label).replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");
        return !new RegExp(`(?<![\\p{L}\\p{N}])${escaped}(?![\\p{L}\\p{N}])`, "u").test(text);
    });
    if (missing.length === 0)
        return copy;
    // Labels are approved questionnaire data. Locale supplies only the list's joining punctuation.
    const choices = new Intl.ListFormat(survey.language, { type: "disjunction" }).format(missing.map((option) => option.label));
    return `${copy} ${choices}`;
}
function joined(part) {
    return part.filter((one) => one !== null && one !== "").join(" ");
}
/**
 * A question as spoken — first asked, or asked again (after an objection, an unclear answer or a request to
 * go back). The explanation comes once, before the first question; a numbered question opens with its
 * ordinal; a scale question ends with its scale words when they are new (the first time, after a question of
 * another kind or other words, and every time it is asked again); a choice question carries its choices.
 */
export function surveyQuestionLineOf(survey, variant, index, repeat) {
    const arm = surveyArmOf(survey, variant);
    const question = arm.questions[index];
    if (question === undefined)
        return null;
    const ordinal = question.numbered ? ordinalOf(survey, numberedIndexOf(arm, index), variant) : null;
    const intro = index === 0 && !repeat ? scaleIntroOf(survey, variant) : null;
    let body;
    switch (question.type) {
        case "scale": {
            const before = arm.questions[index - 1];
            const fresh = repeat || before === undefined || before.type !== "scale" || before.suffix !== question.suffix;
            body = joined([ordinal, question.text, fresh ? question.suffix : null]);
            break;
        }
        case "choice":
            body = joined([ordinal, surveyChoiceLineOf(survey, question)]);
            break;
        case "open":
        case "number":
            body = joined([ordinal, question.text]);
            break;
    }
    return joined([intro, body]);
}
/** A question's own clarification: a choice question's with its choices, an open or number question's line. */
export function surveyClarifyLineOf(survey, question) {
    switch (question.type) {
        case "choice":
            return surveyChoiceLineOf(survey, question, "clarify");
        case "open":
        case "number":
            return question.clarify;
        case "scale":
            return question.suffix;
    }
}
/** The question as first asked (kept under its v1 name: every question of the arm, by index). */
export function statementLineOf(survey, variant, index) {
    return surveyQuestionLineOf(survey, variant, index, false);
}
/** The question asked again (kept under its v1 name). */
export function statementRepeatLineOf(survey, variant, index) {
    return surveyQuestionLineOf(survey, variant, index, true);
}
/**
 * Every fixed line the VoiceAgent can say, in one language.
 *
 * This is what the acknowledgement path pre-synthesises: a survey agent's speech is finite
 * and known before the conversation starts, which is why it can answer instantly and why its
 * TTS bill is a warm cache rather than a per-turn purchase.
 */
export function spokenLineOf(survey) {
    const line = new Set();
    const evening = survey.greetings.evening;
    for (const greeting of [survey.greetings.morning, survey.greetings.afternoon, ...(evening === undefined ? [] : [evening])]) {
        line.add(openingLineOf(survey, greeting));
    }
    // AMENDED 2026-10-02: the consent question asked on its own, when the opening has one.
    const consent = consentQuestionOf(survey);
    if (consent !== null)
        line.add(consent);
    line.add(survey.opening.declined);
    line.add(survey.scale.suffix);
    for (const variant of surveyVariantsOf(survey)) {
        const arm = surveyArmOf(survey, variant);
        for (let index = 0; index < arm.questions.length; index += 1) {
            const first = surveyQuestionLineOf(survey, variant, index, false);
            const again = surveyQuestionLineOf(survey, variant, index, true);
            if (first !== null)
                line.add(first);
            if (again !== null)
                line.add(again);
        }
    }
    for (const question of surveyQuestionListOf(survey)) {
        line.add(surveyClarifyLineOf(survey, question));
        if (question.type === "choice" && question.selection?.more !== undefined)
            line.add(question.selection.more);
    }
    line.add(survey.closing);
    for (const type of SURVEY_OBJECTION) {
        const answer = survey.objections[type];
        if (answer !== undefined && answer.answer !== "")
            line.add(answer.answer);
    }
    line.add(survey.deescalation);
    for (const helper of Object.values(survey.helpers))
        line.add(helper);
    for (const ack of survey.acks)
        line.add(ack);
    return [...line];
}
/**
 * The words the speech recogniser is biased towards, from the questionnaire only.
 *
 * surveybot unions its own `recognition_terms` with the choice option labels. The classifier's
 * keyword stems go in too: they are precisely the word beginnings the rules-first path matches on,
 * so biasing the recogniser towards them is biasing it towards the answers that can be understood
 * without a model call at all.
 */
export function recognitionBiasOf(survey) {
    const term = new Set(survey.recognition_terms);
    for (const question of surveyQuestionListOf(survey)) {
        if (question.type !== "choice")
            continue;
        for (const option of question.options) {
            term.add(option.label);
            for (const keyword of option.keywords)
                term.add(keyword);
        }
    }
    return [...term];
}
// ---- prompt blocks -------------------------------------------------------------------
/**
 * The classification vocabulary. Platform-wide for the survey template, so it belongs in the
 * stable platform preamble rather than in per-agent content.
 *
 * AMENDED 2026-10-11 (ADR 0053): `choice` (any choice question's option ids), `open` (an answer in the
 * respondent's own words) and `number` (a number question's value). `emotion` and `source` stay readable — a
 * reply naming them is read as `choice` at a choice question — but the tool no longer offers them.
 */
export const CLASSIFY_KIND = [
    "affirm",
    "deny",
    "maybe",
    "dont_know",
    "no_answer",
    "choice",
    "open",
    "number",
    "emotion",
    "source",
    "objection",
    "recording",
    "clarification",
    "repeat",
    // AMENDED 2026-10-24 (the owner on the nexus survey, "everything went wrong"): «Με ακούς;» was read as `wait`
    // and answered «Παρακαλώ περιμένετε ένα λεπτό.». A check that the line works is its own kind: the interviewer
    // confirms and asks the current question again. Never a survey answer, so `survey_answer` never files it.
    "hearing_check",
    "wait",
    "stop",
    "continue",
    "back",
    "unclear",
];
/** The kinds the `classify` tool offers the model: every kind but v1's `emotion` and `source`. */
export const CLASSIFY_TOOL_KIND = CLASSIFY_KIND.filter((kind) => kind !== "emotion" && kind !== "source");
export const CLASSIFY_TOOL_NAME = "classify";
export const CLASSIFY_DESCRIPTION = "Report what the respondent meant. Call it exactly once, for every utterance, and write no " +
    "other text.";
/**
 * The `classify` tool, verbatim in spirit from `surveybot/server/classifier.ts`.
 *
 * "Deliberately tiny: the model's answer is ~15 output tokens, and output tokens are what the
 * respondent waits for." It is also deliberately **not** strict-mode: surveybot measured
 * grammar-constrained decoding at roughly a second slower per call, and a second of silence
 * on a cold call is how a respondent hangs up.
 */
export function classifyToolOf() {
    return {
        name: CLASSIFY_TOOL_NAME,
        description: CLASSIFY_DESCRIPTION,
        inputSchema: {
            type: "object",
            required: ["kind", "confident"],
            properties: {
                kind: {
                    type: "string",
                    enum: [...CLASSIFY_TOOL_KIND],
                    description: "What the respondent meant.",
                },
                detail: {
                    type: "string",
                    description: "With choice: the first selected option id from the CURRENT question's configured options. Never invent " +
                        "or group ids. With number: the number the respondent gave, in digits (42, 7.5, -3), converted from words " +
                        "in any language. With objection: one of " +
                        `${SURVEY_OBJECTION.join(", ")}. With back: a question number or previous. Omit otherwise ` +
                        "(an open answer is kept in the respondent's own words; never restate it).",
                },
                answerSupported: {
                    type: "boolean",
                    description: "Required for kind=objection with a known survey FAQ detail: true only if that exact " +
                        "approved answer supplies the fact the respondent requested. False when it is absent, " +
                        "generic or merely related. A matching category is not evidence. Omit for unknown, " +
                        "are_you_robot, hostile and other kinds.",
                },
                answerEvidence: {
                    type: "string",
                    description: "Required for supported survey FAQ answers: the exact quote supplying the specific " +
                        "fact asked for, copied from that approved answer. If no such fact can be quoted, " +
                        "choose detail=unknown. A category match or unrelated quote is not evidence.",
                },
                mentioned: {
                    type: "array",
                    items: { type: "string" },
                    description: "With choice: ALL positively selected configured option ids in spoken order, " +
                        "including detail. Exclude negated/retracted choices. Never truncate to the selection limit.",
                },
                selectionComplete: {
                    type: "boolean",
                    description: "With choice or continue/deny when choices are pending: true ONLY for an explicit refusal of more choices or an explicit request to finish this selection. Requires selectionEvidence. A bare yes or acknowledgement is NEVER finality. Omit otherwise.",
                },
                selectionEvidence: {
                    type: "string",
                    description: "With selectionComplete=true: quote the respondent's exact words declining more choices or finishing this selection (e.g. no more, only this, go to the next question). Never quote the interviewer. Omit for a bare yes, which asks to add a choice.",
                },
                selectionMode: {
                    type: "string", enum: ["replace"],
                    description: "With choice and PENDING SELECTION: replace only when correcting/replacing prior choices; mentioned must then contain the entire corrected selection. Omit to add choices.",
                },
                confident: {
                    type: "boolean",
                    description: "For choice: false if any selected option is uncertain. For number: false if the number is uncertain or " +
                        "approximate in a way the respondent did not settle. For open: true when the reply answers the question " +
                        "asked. For affirm/deny/maybe: true only when the respondent clearly took that position " +
                        "on the question itself. False for jokes, anecdotes, deflections or anything you " +
                        "had to infer.",
                },
                strength: {
                    type: "string",
                    enum: ["strong"],
                    description: "Only with affirm/deny when the respondent explicitly chose strong/complete agreement or disagreement on the stated scale. Omit for a bare yes/no or ordinary agreement; never infer intensity.",
                },
            },
        },
    };
}
/** One question's skeleton for block [2]: its id, code and type, and what bounds its answer. */
function questionSkeletonOf(question) {
    const head = `${question.id}(${question.code}):${question.type}`;
    switch (question.type) {
        case "choice": {
            const selection = question.selection;
            const limit = selection === undefined ? "" : ` min=${String(selection.min)} max=${String(selection.max)} exclusive=${selection.exclusive.join(",") || "none"}`;
            return `${head} options=${question.options.map((option) => option.id).join(",")}${limit}`;
        }
        case "number":
            return `${head} min=${question.min === null ? "none" : String(question.min)} max=${question.max === null ? "none" : String(question.max)} integer=${String(question.integer)}`;
        default:
            return head;
    }
}
/**
 * BLOCK [2] — language-invariant survey content: the instrument's skeleton.
 *
 * Each arm's questions in order with their ids, research codes and types, and what bounds each answer (a
 * choice question's option ids and selection limits, a number question's range). None of it changes with the
 * language, so all of it belongs before the language token.
 */
export function languageInvariantSurveyOf(questionnaire) {
    const language = Object.keys(questionnaire.survey).sort();
    const first = language[0];
    const survey = first === undefined ? undefined : questionnaire.survey[first];
    if (survey === undefined)
        return "SURVEY: no questionnaire configured.";
    const line = [];
    line.push(`LANGUAGES: ${language.join(", ")}`);
    for (const variant of surveyVariantsOf(survey)) {
        line.push(`VARIANT ${variant} QUESTIONS IN ORDER:`);
        for (const question of surveyArmOf(survey, variant).questions)
            line.push(`- ${questionSkeletonOf(question)}`);
    }
    line.push(`OBJECTION TYPES: ${SURVEY_OBJECTION.join(", ")}`);
    return line.join("\n");
}
/** One question's approved text for block [4], with a choice question's options and a number question's unit. */
function questionTextOf(question) {
    const line = [`- ${question.id} (${question.type}): ${question.text}`];
    if (question.type === "scale" && question.suffix !== "")
        line.push(`  scale: ${question.suffix}`);
    if (question.type === "choice") {
        for (const one of question.options)
            line.push(`  - ${one.id} = ${one.label} [${one.keywords.join(" ")}]`);
    }
    if (question.type === "number" && question.unit !== null)
        line.push(`  unit: ${question.unit}`);
    return line;
}
/**
 * BLOCK [4] — language-dependent survey content: the approved text.
 *
 * `null` when the questionnaire has nothing for this language, which makes the language
 * unavailable for this VoiceAgent rather than triggering a fallback to another script.
 */
export function languageDependentSurveyOf(questionnaire, language) {
    const survey = surveyOf(questionnaire, language);
    if (survey === null)
        return null;
    const line = [`SURVEY: ${survey.title}`, `INTERVIEWER: ${survey.interviewer}`];
    line.push(`SCALE: ${survey.scale.suffix}`);
    for (const variant of surveyVariantsOf(survey)) {
        line.push(`VARIANT ${variant} QUESTIONS:`);
        for (const question of surveyArmOf(survey, variant).questions)
            line.push(...questionTextOf(question));
    }
    // The classifier needs the actual available answer, not just a broad objection category.
    // Unknown/AI identity are answered by platform copy; an extracted human-identity claim is not
    // authoritative and must not enter the classifier's evidence about what it can answer.
    line.push("APPROVED SURVEY INFORMATION (use only when the answer addresses the requested fact):");
    for (const type of SURVEY_OBJECTION) {
        if (type === "unknown" || type === "are_you_robot" || type === "hostile")
            continue;
        const objection = survey.objections[type];
        if (objection !== undefined)
            line.push(`- ${type}: answer=${JSON.stringify(objection.answer)}`);
    }
    return line.join("\n");
}
//# sourceMappingURL=derive.js.map